/**
 * The only boundary through which the UI reads or writes personal progress.
 *
 * Today this is localStorage. A future account/cloud adapter can implement the
 * same interface without changing pages. Public knowledge stays in src/data;
 * research-run data stays on the research API and is never mixed into this store.
 */
export interface ProgressRepository {
  readonly kind: 'local' | 'remote';
  load(): unknown;
  save(value: unknown): void;
  clear(): void;
}

export const PROGRESS_STORAGE_KEY = 'causal-atlas:state:v1';

class LocalProgressRepository implements ProgressRepository {
  readonly kind = 'local' as const;

  load(): unknown {
    try {
      const raw = window.localStorage.getItem(PROGRESS_STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      // Private mode, embedded webviews, or corrupted JSON. The caller falls
      // back to defaults; the next save may recover if storage is available.
      return null;
    }
  }

  save(value: unknown): void {
    try {
      window.localStorage.setItem(PROGRESS_STORAGE_KEY, JSON.stringify(value));
    } catch {
      // Local storage is best-effort. No public knowledge is affected.
    }
  }

  clear(): void {
    try {
      window.localStorage.removeItem(PROGRESS_STORAGE_KEY);
    } catch {
      // ignore
    }
  }
}

export function createProgressRepository(): ProgressRepository {
  return new LocalProgressRepository();
}
